import type { MatchResult, NetworkPlayer } from '../network/protocol';

export interface ResultManifestEntry {
  id: string;
  displayName: string;
  local: boolean;
  bot: boolean;
  outcome: MatchResult['outcome'];
  trainNumber: number;
  value: number;
  score: number;
}

export function buildResultManifest(
  results: readonly MatchResult[],
  players: readonly NetworkPlayer[],
  localPlayerID: string,
): ResultManifestEntry[] {
  const playersByID = new Map(players.map((player) => [player.id, player]));
  return results.map((result) => {
    const player = playersByID.get(result.playerId);
    return {
      id: result.playerId,
      displayName: player?.displayName ?? 'LOST SIGNAL',
      local: result.playerId === localPlayerID,
      bot: Boolean(player?.bot),
      outcome: result.outcome,
      trainNumber: Math.max(1, result.extractionStop ?? player?.trainNumber ?? 1),
      value: Math.max(0, result.outcome === 'extracted' ? result.bankedValue : result.lostValue),
      score: Math.max(0, result.score),
    };
  }).sort(compareEntries);
}

function compareEntries(left: ResultManifestEntry, right: ResultManifestEntry): number {
  if (left.outcome !== right.outcome) return left.outcome === 'extracted' ? -1 : 1;
  if (left.value !== right.value) return right.value-left.value;
  if (left.score !== right.score) return right.score-left.score;
  return left.displayName.localeCompare(right.displayName);
}

export function resultManifestMarkup(entries: readonly ResultManifestEntry[]): string {
  return entries.map((entry, index) => {
    const classes = [entry.local ? 'is-local' : '', entry.bot ? 'is-bot' : '', entry.outcome === 'lost' ? 'is-lost' : 'is-banked']
      .filter(Boolean).join(' ');
    const badge = entry.local ? 'YOU' : entry.bot ? 'BOT' : 'RUNNER';
    return `<li class="${classes}" data-result-runner-id="${escapeMarkup(entry.id)}">
      <span class="result-manifest__rank">${String(index+1).padStart(2, '0')}</span>
      <span class="result-manifest__identity"><b>${escapeMarkup(entry.displayName)}</b><small>${badge}</small></span>
      <span class="result-manifest__outcome"><small>OUTCOME</small><b>${entry.outcome === 'extracted' ? 'BANKED' : 'LOST'}</b></span>
      <span><small>TRAIN</small><b>${entry.trainNumber}</b></span>
      <span class="result-manifest__value"><small>${entry.outcome === 'extracted' ? 'SECURED' : 'FORFEIT'}</small><b>₵${entry.value}</b></span>
      <span><small>SCORE</small><b>${entry.score}</b></span>
    </li>`;
  }).join('');
}

export class ResultManifest {
  private readonly panel: HTMLElement;
  private readonly summary: HTMLElement;
  private readonly list: HTMLOListElement;
  private renderKey = '';

  public constructor(root: ParentNode) {
    this.panel = requireElement<HTMLElement>(root, '[data-result-manifest]');
    this.summary = requireElement<HTMLElement>(root, '[data-result-manifest-summary]');
    this.list = requireElement<HTMLOListElement>(root, '[data-result-manifest-list]');
  }

  public reset(): void {
    this.renderKey = '';
    this.panel.hidden = true;
    this.summary.textContent = 'AWAITING OUTCOMES';
    this.list.replaceChildren();
  }

  public update(results: readonly MatchResult[], players: readonly NetworkPlayer[], localPlayerID: string): void {
    if (results.length === 0) return;
    const entries = buildResultManifest(results, players, localPlayerID);
    const key = entries.map((entry) => [
      entry.id, entry.displayName, entry.local ? 1 : 0, entry.bot ? 1 : 0,
      entry.outcome, entry.trainNumber, entry.value, entry.score,
    ].join(':')).join('|');
    if (key === this.renderKey) return;
    this.renderKey = key;
    const banked = entries.filter((entry) => entry.outcome === 'extracted').length;
    const lost = entries.length-banked;
    const pending = Math.max(0, players.length-entries.length);
    this.summary.textContent = `${banked} BANKED // ${lost} LOST${pending > 0 ? ` // ${pending} RUNNING` : ''}`;
    this.list.innerHTML = resultManifestMarkup(entries);
    this.panel.hidden = false;
  }
}

function escapeMarkup(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    "'": '&#39;',
    '"': '&quot;',
  })[character] ?? character);
}

function requireElement<T extends Element>(root: ParentNode, selector: string): T {
  const element = root.querySelector<T>(selector);
  if (!element) throw new Error(`Missing result manifest element: ${selector}`);
  return element;
}
